import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    extractionRunId: String,
    documentType: String, // 'proposal' or 'invoice'

    // URLs
    startUrl: String,
    statusUrl: String,
    cancelUrl: String,
    redirectUrl: String,

    // Proposal-specific
    reviewUrl: String,
    proposalTemplateId: String,
    includeTemplateLineItems: { type: Boolean, default: false },

    // DOM selectors
    containerSelector: String,
    loaderPartial: String
  }

  static targets = ["message", "loader"]

  connect() {
    this.pollInterval = null
    this.startProcessing()
  }

  disconnect() {
    this.stopPolling()
  }

  startProcessing() {
    fetch(this.startUrlValue)
      .then(response => response.json())
      .then(data => {
        this.extractionRunIdValue = data.extraction_run_id
        this.startPolling()
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Error starting processing:', error)
        this.updateMessage('An error occurred. Please refresh the page and try again.')
      })
  }

  cancelProcessing(event) {
    event.preventDefault()

    // eslint-disable-next-line no-alert
    if (!confirm('Are you sure you want to cancel the processing?')) {
      return
    }

    this.stopPolling()

    if (this.extractionRunIdValue) {
      const body = new URLSearchParams({
        extraction_run_id: this.extractionRunIdValue
      })

      fetch(this.cancelUrlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.csrfToken,
          Accept: 'application/json'
        },
        body
      })
      .finally(() => location.reload())
    } else {
      location.reload()
    }
  }

  startPolling() {
    const maxPollingDuration = 120000 // 2 minutes
    const pollIntervalTime = 2000 // 2 seconds
    const startTime = Date.now()

    this.pollInterval = setInterval(() => {
      const elapsedTime = Date.now() - startTime

      if (elapsedTime > maxPollingDuration) {
        this.stopPolling()
        this.updateMessage('Please press cancel and try again')
        return
      }

      fetch(this.statusUrlValue)
        .then(response => response.json())
        .then(data => {
          this.updateMessage(data.message)

          if (data.step === 'complete') {
            this.stopPolling()
            this.handleCompletion()
          }

          if (data.step === 'error') {
            this.stopPolling()
            this.updateMessage(data.message || 'An error occurred. Please try again.')
          }
        })
        .catch(error => {
          this.stopPolling()
          this.updateMessage('An error occurred. Please refresh the page.')
        })
    }, pollIntervalTime)
  }

  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }
  }

  handleCompletion() {
    if (this.documentTypeValue === 'proposal') {
      this.showReviewModal()
    } else {
      window.location.href = this.buildRedirectUrl()
    }
  }

  showReviewModal() {
    const url = new URL(this.reviewUrlValue.replace('EXTRACTION_RUN_ID_PLACEHOLDER', this.extractionRunIdValue), window.location.origin)
    if (this.hasProposalTemplateIdValue) url.searchParams.set('proposal_template_id', this.proposalTemplateIdValue)
    if (this.hasIncludeTemplateLineItemsValue) url.searchParams.set('include_template_line_items', this.includeTemplateLineItemsValue)
    const reviewUrl = url.toString()

    fetch(reviewUrl)
      .then(response => response.text())
      .then(html => {
        // Close loading modal and reset form
        const $modal = $('#autofill_proposals')
        $modal.one('hidden.bs.modal', () => {
          $(this.containerSelectorValue).html(this.loaderPartialValue)
          window.activatePushedContent?.(document.querySelector(this.containerSelectorValue))
        })
        $modal.modal('hide')

        // Show review modal
        $('#review_autofill_modal').remove()
        $('body').append(html)
        $('#review_autofill_modal').modal('show')
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Error loading review modal:', error)
        this.updateMessage('An error occurred loading the review modal.')
      })
  }

  buildRedirectUrl() {
    const url = new URL(this.redirectUrlValue, window.location.origin)
    url.searchParams.set('extraction_run_id', this.extractionRunIdValue)
    if (this.hasProposalTemplateIdValue) url.searchParams.set('proposal_template_id', this.proposalTemplateIdValue)
    if (this.hasIncludeTemplateLineItemsValue) url.searchParams.set('include_template_line_items', this.includeTemplateLineItemsValue)
    return url.toString()
  }

  updateMessage(message) {
    if (this.hasMessageTarget) {
      this.messageTarget.textContent = message
    } else {
      // Fallback for existing code
      const progressMessage = document.querySelector('.progress-message')
      if (progressMessage) {
        progressMessage.textContent = message
      }
    }
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]').content
  }
}
